// Криейтър: начален екран — статус на профила, условията на програмата,
// текущата седмица (добавяне на видеа, предаване) и историята по седмици.

import React, { useCallback, useState } from 'react';
import { Linking, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { api, API_URL, money } from '../../api';
import { useAuth } from '../../AuthContext';
import { showError, showSuccess } from '../../toast';
import { Badge, Button, Card, COLORS, Divider, Input, Label, Muted, Row, Screen, SectionLabel, Select, Title } from '../../ui';
import { getLocale, t } from '../../i18n';

const platforms = () => ['TikTok', 'Instagram', 'YouTube', 'Facebook', t('Друга')];

const weekStatus = (): Record<string, [string, string]> => ({
  DRAFT: [t('Чернова'), COLORS.muted],
  SUBMITTED: [t('Чака преглед'), '#D97706'],
  APPROVED: [t('Одобрена ✓'), COLORS.success],
  REJECTED: [t('Отхвърлена'), COLORS.danger],
});

const weekLabel = (iso: string) => {
  const start = new Date(iso);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  const f = (d: Date) => d.toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG', { day: 'numeric', month: 'short' });
  return `${f(start)} – ${f(end)}`;
};

export default function CreatorHomeScreen() {
  const { user } = useAuth();
  const [me, setMe] = useState<any>(null);
  const [ideas, setIdeas] = useState<any[]>([]);
  const [weeks, setWeeks] = useState<any[]>([]);
  const [currentWeekStart, setCurrentWeekStart] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [platform, setPlatform] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const WEEK_STATUS = weekStatus();

  const load = useCallback(() => {
    api('/creator/me').then(setMe).catch((e) => showError(e.message));
    api('/creator/weeks')
      .then((r) => {
        setWeeks(r.weeks);
        setCurrentWeekStart(r.currentWeekStart);
      })
      .catch(() => {});
    api('/creator/ideas')
      .then((r) => setIdeas(r.ideas))
      .catch(() => {});
  }, []);
  useFocusEffect(load);

  if (!me) return <Screen><Muted>{t('Зареждане…')}</Muted></Screen>;

  const { profile, settings } = me;
  const currentWeek = weeks.find((w) => currentWeekStart && new Date(w.weekStart).getTime() === new Date(currentWeekStart).getTime());
  const pastWeeks = weeks.filter((w) => w !== currentWeek);

  const addVideo = async () => {
    setLoading(true);
    try {
      await api('/creator/videos', { body: { url: url.trim(), platform: platform ?? 'Друга', ...(note.trim() ? { note: note.trim() } : {}) } });
      setUrl('');
      setNote('');
      showSuccess(t('Видеото е добавено към текущата седмица.'));
      load();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const submitWeek = async (weekId: string) => {
    try {
      await api(`/creator/weeks/${weekId}/submit`, { body: {} });
      showSuccess(t('Седмицата е предадена за преглед.'));
      load();
    } catch (e: any) {
      showError(e.message);
    }
  };

  return (
    <Screen>
      <Muted>{t('Здравейте,')}</Muted>
      <Title>{user?.firstName} 🎬</Title>

      {/* Статус на профила */}
      {profile.status === 'PENDING_APPROVAL' && (
        <Card style={{ borderColor: '#D97706' }}>
          <Text style={{ fontWeight: '700', color: COLORS.text }}>{t('Профилът чака одобрение')}</Text>
          <Muted>{t('Ще получите известие, щом бъде прегледан. Дотогава не може да подавате видеа.')}</Muted>
        </Card>
      )}
      {profile.status === 'REJECTED' && (
        <Card style={{ borderColor: COLORS.danger }}>
          <Text style={{ fontWeight: '700', color: COLORS.danger }}>{t('Профилът не е одобрен')}</Text>
          {profile.rejectionReason ? <Muted>{t('Причина: {reason}', { reason: profile.rejectionReason })}</Muted> : null}
        </Card>
      )}

      {/* Балансът накратко (детайлите са в раздел Пари) */}
      <Card>
        <Row left={t('Баланс')} right={money(profile.balanceCents)} />
        <Muted>
          {t('{reward} € на одобрена седмица · теглене от {min} €', {
            reward: (settings.weeklyRewardCents / 100).toFixed(0),
            min: (settings.minWithdrawCents / 100).toFixed(0),
          })}
        </Muted>
      </Card>

      {/* Идеи за видеа: актуалните промоции от админа */}
      {ideas.length > 0 && (
        <Card style={{ borderColor: COLORS.primary, borderWidth: 1.5 }}>
          <SectionLabel>{t('💡 Идеи за видеа')}</SectionLabel>
          <Muted>{t('Актуални промоции и теми — видеата по тях се броят като всички останали.')}</Muted>
          {ideas.map((i, idx) => (
            <View key={i.id} style={{ marginTop: idx === 0 ? 8 : 12 }}>
              <Text style={{ fontWeight: '700', color: COLORS.text, fontSize: 15 }}>{i.title}</Text>
              <Text style={{ color: COLORS.text, marginTop: 2, lineHeight: 20 }}>{i.description}</Text>
              <Muted>{t('от {date}', { date: new Date(i.createdAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })}</Muted>
            </View>
          ))}
        </Card>
      )}

      {/* Условия на програмата */}
      <Card>
        <SectionLabel>{t('Условия за седмицата')}</SectionLabel>
        <Muted>
          {t('• Поне {n} видеа, публикувани в поне {m} различни мрежи', { n: settings.videosPerWeek, m: settings.minNetworks })}{'\n'}
          {t('• Поне {n} гледания на видео', { n: settings.minViewsPerVideo })}{'\n'}
          {t('• При повече видеа — общо поне {n} гледания за седмицата', { n: settings.minWeeklyViews })}{'\n'}
          {t('• Прегледът е ръчен — подавайте линк към всяко видео')}
        </Muted>
        <Button
          title={t('Пълни условия за участие →')}
          variant="secondary"
          onPress={() => Linking.openURL(`${API_URL}/creator-terms`)}
        />
      </Card>

      {profile.status === 'APPROVED' && (
        <>
          <SectionLabel>{t('Текуща седмица')} {currentWeekStart ? `(${weekLabel(currentWeekStart)})` : ''}</SectionLabel>
          <Card>
            {(currentWeek?.videos ?? []).map((v: any) => (
              <View key={v.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: COLORS.border }}>
                <View style={{ flex: 1 }}>
                  <Text style={{ color: COLORS.text, fontWeight: '600' }}>{v.platform}</Text>
                  <Text style={{ color: COLORS.muted, fontSize: 12 }} numberOfLines={1}>{v.url}</Text>
                </View>
                <Button
                  title="✕"
                  variant="danger"
                  onPress={async () => {
                    try {
                      await api(`/creator/videos/${v.id}`, { method: 'DELETE' });
                      load();
                    } catch (e: any) {
                      showError(e.message);
                    }
                  }}
                />
              </View>
            ))}
            {(currentWeek?.videos?.length ?? 0) === 0 && <Muted>{t('Още няма видеа тази седмица.')}</Muted>}

            <Label>{t('Линк към видеото')}</Label>
            <Input value={url} onChangeText={setUrl} autoCapitalize="none" placeholder="https://tiktok.com/@…/video/…" />
            <Label>{t('Платформа')}</Label>
            <Select placeholder={t('Изберете платформа…')} options={platforms()} value={platform} onChange={setPlatform} />
            <Label>{t('Бележка (по избор)')}</Label>
            <Input value={note} onChangeText={setNote} placeholder={t('напр. колаборация с...')} />
            <Button title={t('+ Добави видео')} onPress={addVideo} disabled={!url.trim() || !platform} loading={loading} />

            {currentWeek && currentWeek.videos.length > 0 && currentWeek.status === 'DRAFT' && (
              <>
                <Divider />
                <Muted>
                  {t('{n} видео(а) · {m} мрежи. Може да предадете сега или седмицата ще се предаде сама в края си.', {
                    n: currentWeek.videos.length,
                    m: new Set(currentWeek.videos.map((v: any) => v.platform)).size,
                  })}
                </Muted>
                <Button title={t('Предай седмицата за преглед')} variant="secondary" onPress={() => submitWeek(currentWeek.id)} />
              </>
            )}
            {currentWeek?.status === 'SUBMITTED' && <Badge text={t('Предадена — чака преглед')} color="#D97706" />}
          </Card>
        </>
      )}

      <SectionLabel>{t('История')}</SectionLabel>
      {pastWeeks.length === 0 && (
        <Card><Muted>{t('Още няма минали седмици.')}</Muted></Card>
      )}
      {pastWeeks.map((w) => (
        <Card key={w.id}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontWeight: '700', color: COLORS.text }}>{weekLabel(w.weekStart)}</Text>
            <Badge text={WEEK_STATUS[w.status]?.[0] ?? w.status} color={WEEK_STATUS[w.status]?.[1]} />
          </View>
          <Muted>{t('{n} видео(а)', { n: w.videos.length })}</Muted>
          {w.status === 'APPROVED' && w.payoutCents ? <Muted>{t('+ {amount} в баланса', { amount: money(w.payoutCents) })}</Muted> : null}
          {w.status === 'REJECTED' && w.rejectionReason ? <Muted>{t('Причина: {reason}', { reason: w.rejectionReason })}</Muted> : null}
          {w.status === 'DRAFT' && w.videos.length > 0 && (
            <Button title={t('Предай за преглед')} variant="secondary" onPress={() => submitWeek(w.id)} />
          )}
        </Card>
      ))}
    </Screen>
  );
}
